import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, message, Typography, List, Row, Col, Layout, Pagination, Space, Breadcrumb, Input, Flex, Grid, Popover, Tooltip, Button } from 'antd';
import { createOrUpdateBusinessInfoEntry, createOrUpdateQAEntry, getEntries, getKnowledgeDatabase, deleteEntry } from '../KnowledgeDatabaseServices';
import { resolveBackendMessage } from '../../../Utils/apiMessages';
import { useGlobal } from '../../Shared/GlobalContext/GlobalContext';
import EntryItem from './EntryItem/EntryItem';
import { Content } from 'antd/es/layout/layout';
import DatabaseKPIs from './DatabaseKPIs/DatabaseKPIs';
import NewEntryForm from './NewEntryForm/NewEntryForm';
import { DatabaseOutlined, SearchOutlined, EllipsisOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import SharedFilterBar from '../../Shared/Filters/SharedFilterBar';
import { knowledgeEntryFilterFields, knowledgeEntrySortFields } from '../../Shared/Filters/filterConfig';
import useFilterOptionFields, { FILTER_OPTION_RESOURCE } from '../../Shared/Filters/useFilterOptionFields';
import { Resource, Action } from '../../../Constants/socket';
import useSocketSubscription from '../../../Hooks/useSocketSubscription';
import useSocketEvent from '../../../Hooks/useSocketEvent';
import useVisualHighlight from '../../../Hooks/useVisualHighlight';
import ExportButton from '../../DataExportImport/ExportButton';
import ImportButton from '../../DataExportImport/ImportButton';

const { Text, Title } = Typography;

const KNOWLEDGE_ENTRY_FILTER_OPTION_RESOURCES = {
    knowledge_base_id: FILTER_OPTION_RESOURCE.KNOWLEDGE_BASE
};

const KnowledgeDatabaseDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const { loading, setLoading } = useGlobal();
    const [entries, setEntries] = useState([]);
    const [databaseInfo, setDatabaseInfo] = useState(null);
    const [createEntryLoading, setCreateEntryLoading] = useState(false);
    const [fetchingDatabaseInfo, setFetchingDatabaseInfo] = useState(false);

    // Visual highlights for real-time socket events
    const { highlightRow, getHighlightClass } = useVisualHighlight();

    // Filter and search state
    const [searchText, setSearchText] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [entryChipFilters, setEntryChipFilters] = useState([]);
    const [entryNormalizedFilters, setEntryNormalizedFilters] = useState({});
    const [sortBy, setSortBy] = useState(undefined);
    const [sortDirection, setSortDirection] = useState(undefined);
    const [isMoreActionsOpen, setIsMoreActionsOpen] = useState(false);
    const [activeEntryId, setActiveEntryId] = useState(null);
    const screens = Grid.useBreakpoint();
    const filterFields = useFilterOptionFields(knowledgeEntryFilterFields, KNOWLEDGE_ENTRY_FILTER_OPTION_RESOURCES);

    const formRef = useRef(null);

    const [paginationMetadata, setPaginationMetadata] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    const fetchEntries = useCallback(async (databaseId, page = 1, size = 10, search = '', filters = {}, sort_by, sort_direction) => {
        try {
            setLoading(true);
            const { data, success, metadata, ...payload } = await getEntries(databaseId, page, size, search, filters, sort_by, sort_direction);
            if (success) {
                setEntries(data);
                setPaginationMetadata({
                    current: metadata.page,
                    pageSize: metadata.page_size,
                    total: metadata.total
                });
            } else {
                const text = resolveBackendMessage(t, payload);
                if (text) message.error(text);
            }
        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('error_fetching_entries'));
        } finally {
            setLoading(false);
        }
    }, [t, setLoading]);

    const debouncedFetch = useRef(
        debounce((databaseId, page, size, search, filters, sort_by, sort_direction) => {
            fetchEntries(databaseId, page, size, search, filters, sort_by, sort_direction);
        }, 500)
    ).current;

    useEffect(() => {
        return () => debouncedFetch.cancel();
    }, []);

    const handlePageChange = (page, pageSize) => {
        fetchEntries(id, page, pageSize, searchText, entryNormalizedFilters, sortBy, sortDirection);
    };

    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchText(value);
        setPaginationMetadata(prev => ({ ...prev, current: 1 }));
        debouncedFetch(id, 1, paginationMetadata.pageSize, value, entryNormalizedFilters, sortBy, sortDirection);
    };

    const handleSortChange = ({ sort_by, sort_direction }) => {
        setSortBy(sort_by);
        setSortDirection(sort_direction);
        setPaginationMetadata(prev => ({ ...prev, current: 1 }));
        fetchEntries(id, 1, paginationMetadata.pageSize, searchText, entryNormalizedFilters, sort_by, sort_direction);
    };

    const handleFilterChange = (nextFilters, normalized) => {
        setEntryChipFilters(nextFilters);
        setEntryNormalizedFilters(normalized);
        setPaginationMetadata(prev => ({ ...prev, current: 1 }));
        debouncedFetch(id, 1, paginationMetadata.pageSize, searchText, normalized, sortBy, sortDirection);
    };

    const fetchDatabaseInfo = useCallback(async (localId, useGlobalLoading = true) => {
        if (!localId) {
            message.error(t('no_database_id_provided'));
            return;
        }
        try {
            if (useGlobalLoading) {
                setLoading(true);
            } else {
                setFetchingDatabaseInfo(true);
            }
            const { data } = await getKnowledgeDatabase(localId);
            setDatabaseInfo(data);
        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('error_fetching_database_information'));
        } finally {
            if (useGlobalLoading) {
                setLoading(false);
            } else {
                setFetchingDatabaseInfo(false);
            }
        }
    }, [setLoading, t]);

    const entriesRef = useRef([]);
    useEffect(() => {
        entriesRef.current = entries;
    }, [entries]);

    const kbIdArray = useMemo(() => id ? [id] : null, [id]);
    useSocketSubscription(Resource.KNOWLEDGE_BASE, kbIdArray);

    const entryIdsArray = useMemo(() => entriesRef.current.map(e => e.id).filter(Boolean), [entriesRef.current]);
    useSocketSubscription(Resource.KNOWLEDGE_ENTRY, entryIdsArray.length > 0 ? entryIdsArray : null);

    useSocketEvent(Resource.KNOWLEDGE_BASE, Action.UPDATE, useCallback((data) => {
        if (data?.id === id) {
            setDatabaseInfo(prev => prev ? { ...prev, ...data } : prev);
        }
    }, [id]));

    useSocketEvent(Resource.KNOWLEDGE_BASE, Action.DELETE, useCallback((data) => {
        if (data?.id === id) {
            message.warning(t('knowledge_base_deleted'));
            navigate('/knowledge-base');
        }
    }, [id, navigate, t]));

    useSocketEvent(Resource.KNOWLEDGE_ENTRY, Action.CREATE, useCallback((data) => {
        if (data?.knowledge_base_id === id) {
            fetchEntries(id, Math.max(1, paginationMetadata.current), paginationMetadata.pageSize, searchText, entryNormalizedFilters, sortBy, sortDirection);
            fetchDatabaseInfo(id, false);
        }
        if (data?.id) highlightRow(data.id);
    }, [id, paginationMetadata.current, paginationMetadata.pageSize, searchText, entryNormalizedFilters, sortBy, sortDirection, fetchEntries, fetchDatabaseInfo, highlightRow]));

    useSocketEvent(Resource.KNOWLEDGE_ENTRY, Action.UPDATE, useCallback((data) => {
        if (data?.knowledge_base_id === id) {
            setEntries(prev => prev.map(e => e.id === data.id ? { ...e, ...data } : e));
        }
        if (data?.id) highlightRow(data.id, 'UPDATE');
    }, [id, highlightRow]));

    useSocketEvent(Resource.KNOWLEDGE_ENTRY, Action.DELETE, useCallback((data) => {
        if (entriesRef.current.some(e => e.id === data.id)) {
            fetchEntries(id, Math.max(1, paginationMetadata.current), paginationMetadata.pageSize, searchText, entryNormalizedFilters, sortBy, sortDirection);
            fetchDatabaseInfo(id, false);
        }
    }, [id, paginationMetadata.current, paginationMetadata.pageSize, searchText, entryNormalizedFilters, sortBy, sortDirection, fetchEntries, fetchDatabaseInfo]));

    const handleCreateOrUpdateEntry = useCallback(async (values) => {
        if (!id) {
            message.error(t('no_database_id_provided'));
            return;
        }
        try {
            setCreateEntryLoading(true);
            const api = values.entryType === 'qa' ? createOrUpdateQAEntry : createOrUpdateBusinessInfoEntry;
            const entryData = values.entryType === 'qa'
                ? {
                    knowledge_base_id: id,
                    question: values.question,
                    answer: values.answer
                }
                : {
                    knowledge_base_id: id,
                    content: values.content
                };
            // create new entry only; updates happen inline in EntryItem
            const payload = await api(entryData);
            await fetchEntries(id, paginationMetadata.current, paginationMetadata.pageSize, searchText, entryNormalizedFilters, sortBy, sortDirection);
            await fetchDatabaseInfo(id);
            {
                const text = resolveBackendMessage(t, payload);
                if (text) message.success(text);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                message.error(t('unauthorized_access'));
            } else {
                message.error(resolveBackendMessage(t, error?.response?.data) || t('error_creating_updating_entry'));
            }
        } finally {
            setCreateEntryLoading(false);
        }
    }, [id,
        fetchEntries, fetchDatabaseInfo, paginationMetadata]);

    const handleInlineUpdate = useCallback(async (entry, values) => {
        try {
            setLoading(true);
            const payload = entry.entry_type === 'question_answer'
                ? await createOrUpdateQAEntry({ id: entry.id, question: values.question, answer: values.answer })
                : await createOrUpdateBusinessInfoEntry({ id: entry.id, content: values.content });
            await fetchEntries(id, paginationMetadata.current, paginationMetadata.pageSize, searchText, entryNormalizedFilters, sortBy, sortDirection);
            await fetchDatabaseInfo(id);
            {
                const text = resolveBackendMessage(t, payload);
                if (text) message.success(text);
            }
        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('error_updating_entry'));
        } finally {
            setLoading(false);
        }
    }, [id, fetchEntries, fetchDatabaseInfo, paginationMetadata, setLoading, t]);

    const handleDeleteEntry = async (entryId) => {
        try {
            const { success, ...payload } = await deleteEntry(entryId);
            if (success) {
                const text = resolveBackendMessage(t, payload);
                if (text) message.success(text);
                const newPage = entries.length === 1 && paginationMetadata.current > 1
                    ? paginationMetadata.current - 1
                    : paginationMetadata.current;
                await fetchEntries(id, newPage, paginationMetadata.pageSize, searchText, entryNormalizedFilters, sortBy, sortDirection);
                await fetchDatabaseInfo(id);
            } else {
                const text = resolveBackendMessage(t, payload);
                if (text) message.error(text);
            }
        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('error_deleting_entry'));
        }
    };

    const handleToggleEntry = async (entry, isDisabled) => {
        try {
            setLoading(true);
            const payload = entry.entry_type === 'question_answer'
                ? await createOrUpdateQAEntry({ id: entry.id, is_disabled: isDisabled })
                : await createOrUpdateBusinessInfoEntry({ id: entry.id, is_disabled: isDisabled });
            await fetchEntries(id, paginationMetadata.current, paginationMetadata.pageSize, searchText, entryNormalizedFilters, sortBy, sortDirection);
            await fetchDatabaseInfo(id);
            {
                const text = resolveBackendMessage(t, payload);
                if (text) message.success(text);
            }
        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('error_updating_entry'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchDatabaseInfo(id);
            fetchEntries(id, 1, paginationMetadata.pageSize, '', {}, undefined, undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, fetchDatabaseInfo, fetchEntries]);

    if (!id) {
        return (
            <Layout>
                <Content>
                    <Row justify="center">
                        <Col xs={24} md={12}>
                            <Card>
                                <Text type="danger">{t('no_database_id_provided_check_url')}</Text>
                            </Card>
                        </Col>
                    </Row>
                </Content>
            </Layout>
        );
    }
    return (
        <Layout>
            <Content>
                <Row style={{ marginBottom: '8px' }}>
                    <Col span={24}>
                        <Breadcrumb
                            items={[
                                {
                                    title: <Link to="/knowledge-base">
                                        <DatabaseOutlined /> {t('knowledge_bases')}
                                    </Link>,
                                },
                                {
                                    title: databaseInfo?.name || t('loading'),
                                },
                            ]}
                        />
                    </Col>
                </Row>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <DatabaseKPIs databaseInfo={databaseInfo} refresh={() => fetchDatabaseInfo(id, false)} />
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={8}>
                            <NewEntryForm
                                onSubmit={handleCreateOrUpdateEntry}
                                loading={createEntryLoading}
                                formRef={formRef}
                                knowledgeBaseId={id}
                            />
                        </Col>
                        <Col xs={24} md={16}>
                                <Card
                                    bordered={false}
                                    title={t('existing_entries')}
                                    extra={
                                        <Popover
                                            trigger="click"
                                            placement="bottomRight"
                                            open={isMoreActionsOpen}
                                            onOpenChange={setIsMoreActionsOpen}
                                            content={
                                                <Space direction="vertical" style={{ minWidth: 140 }} onClickCapture={() => setIsMoreActionsOpen(false)}>
                                                    <ImportButton resource={Resource.KNOWLEDGE_BASE} type="text" size="small" />
                                                    <ExportButton 
                                                        resource={Resource.KNOWLEDGE_BASE} 
                                                        filters={{ id }} 
                                                        type="text" 
                                                        size="small" 
                                                    />
                                                </Space>
                                            }
                                        >
                                            <Tooltip title={t('more_actions')}>
                                                <Button
                                                    type="text"
                                                    icon={<EllipsisOutlined />}
                                                    size="medium"
                                                />
                                            </Tooltip>
                                        </Popover>
                                    }
                                    style={{
                                        borderRadius: '8px'
                                    }}>
                                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                        <Flex
                                            gap="middle"
                                            wrap="wrap"
                                            align="center"
                                            justify="space-between"
                                        >
                                            <Flex
                                                flex={{ xs: '100%', md: 1 }}
                                                style={{ flexGrow: 1 }}
                                            >
                                                <Input
                                                    prefix={<SearchOutlined />}
                                                    placeholder={t('search')}
                                                    size="medium"
                                                    onChange={handleSearchChange}
                                                    onFocus={() => setIsInputFocused(true)}
                                                    onBlur={() => setIsInputFocused(false)}
                                                    style={{
                                                        width: isInputFocused ? '100%' : (screens.xs ? '100%' : 300),
                                                        transition: 'width 0.4s ease-in-out'
                                                    }}
                                                    value={searchText}
                                                />
                                            </Flex>

                                            <SharedFilterBar
                                                fields={filterFields}
                                                value={entryChipFilters}
                                                onChange={handleFilterChange}
                                                sortFields={knowledgeEntrySortFields}
                                                sortValue={{ sort_by: sortBy, sort_direction: sortDirection }}
                                                onSortChange={handleSortChange}
                                            />
                                        </Flex>

                                        <List
                                            dataSource={entries}
                                            renderItem={item => (
                                                <EntryItem
                                                    key={item?.id}
                                                    item={item}
                                                    onDelete={() => handleDeleteEntry(item.id)}
                                                    onToggle={handleToggleEntry}
                                                    onUpdate={handleInlineUpdate}
                                                    updating={loading}
                                                    knowledgeBaseId={id}
                                                    isActive={activeEntryId === item.id}
                                                    onActivate={() => setActiveEntryId(item.id)}
                                                    onDeactivate={() => setActiveEntryId(null)}
                                                    className={getHighlightClass ? getHighlightClass(item.id, 'socket-highlight-target-card') : ''}
                                                />
                                            )}
                                        />
                                        <Row justify="end">
                                            <Pagination
                                                size="small"
                                                simple={screens.xs}
                                                current={paginationMetadata.current}
                                                pageSize={paginationMetadata.pageSize}
                                                total={paginationMetadata.total}
                                                onChange={handlePageChange}
                                                showSizeChanger={!screens.xs}
                                                showQuickJumper={!screens.xs}
                                                pageSizeOptions={['10', '25', '50', '100']}
                                                showTotal={(total, range) =>
                                                    `${range?.[0] || 0}-${range?.[1] || 0} ${t('of')} ${total} ${t('items')}`
                                                }
                                            />
                                        </Row>
                                    </Space>
                                </Card>
                        </Col>
                    </Row>
                </Space>
            </Content>
        </Layout>
    );
};

export default KnowledgeDatabaseDetail;
